<script setup lang="ts">
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogDescription,
} from '@headlessui/vue'
import IconChevronDown from '~/components/icons/IconChevronDown'

const props = defineProps<{
  event: EventDescription
}>()
const { isMobile } = useDevice()
const isPreviewModalOpen = ref(false)
const togglePreviewModalOpen = () => {
  isPreviewModalOpen.value = !isPreviewModalOpen.value
}
const featuredImages = computed(() => {
  return (
    [
      ...eventGetters.getFeaturedImages(props.event).map((image: any) => ({
        ...image,
        src: image.cropped || image.original,
      })),
      ...eventGetters.getNonFeaturedImages(props.event).map((image: any) => ({
        ...image,
        src: image.cropped || image.original,
      })),
    ] || []
  )
})

const principalImage = computed(() => {
  const image = eventGetters.getPrincipalImage(props.event)
  const src = image?.cropped || image?.original

  return image ? { ...image, src } : {}
})
</script>
<template>
  <div class="relative">
    <NuxtLazyHydrate when-idle>
      <EventGalleryActionButtons
        :event="event"
        :is-giftcard="eventGetters.isGiftcardEvent(event)"
        @click:open-preview-modal="togglePreviewModalOpen"
      />
    </NuxtLazyHydrate>

    <EventGalleryGrid
      :principal-image="principalImage"
      :featured-images="featuredImages"
      :has-vertical-video="Boolean(eventGetters.getPortraitVideo(event))"
      @click:image="togglePreviewModalOpen"
    >
      <template #main-image>
        <LazyEventGalleryVideoPlayer
          v-if="!isMobile && eventGetters.getLandscapeVideo(event)"
          :is-mobile="isMobile"
          class="w-2/3 cursor-pointer"
          :allow-fullscreen="true"
          :source="eventGetters.getLandscapeVideo(event).original"
          :source-stream="eventGetters.getLandscapeVideo(event).streaming_url"
          :poster="eventGetters.getLandscapeVideo(event).thumb"
          orientation="horizontal"
        />
      </template>
      <template #vertical-slot>
        <LazyEventGalleryVideoPlayer
          v-if="eventGetters.getPortraitVideo(event)"
          :is-mobile="isMobile"
          class="w-full cursor-pointer"
          :allow-fullscreen="true"
          :source="eventGetters.getPortraitVideo(event).original"
          :source-stream="eventGetters.getPortraitVideo(event).streaming_url"
          :poster="eventGetters.getPortraitVideo(event).thumb"
          orientation="vertical"
        />
      </template>
    </EventGalleryGrid>

    <ClientOnly>
      <TransitionRoot as="template" :show="isPreviewModalOpen">
        <Dialog class="relative z-[100]" @close="togglePreviewModalOpen">
          <div class="fixed inset-0 flex w-screen items-center justify-center">
            <TransitionChild
              enter="duration-300 ease-out"
              enter-from="opacity-0 translate-y-full"
              enter-to="opacity-100 translate-y-0"
              leave="duration-200 ease-in"
              leave-from="opacity-100 translate-y-0"
              leave-to="opacity-0 translate-y-full"
              as="template"
            >
              <DialogPanel class="flex flex-col w-full h-full bg-white">
                <DialogTitle class="p-4 text-base">
                  <div>
                    <Button
                      color="tertiary"
                      class="px-2 border border-gray-300 !rounded-full"
                      @click.prevent="togglePreviewModalOpen"
                    >
                      <IconChevronDown class="rotate-90" />
                    </Button>
                  </div>
                </DialogTitle>
                <DialogDescription class="p-4 overflow-x-auto">
                  <EventGalleryPreview :event="event" />
                </DialogDescription>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </TransitionRoot>
    </ClientOnly>
  </div>
</template>
